/**
 * Cross-tab signal for "Final Submit finished in the preview tab".
 *
 * Generate Preview opens the profile PDF in a NEW tab and the Submit button lives there.
 * Without a signal the original tab is left on the stale Final-Submit form while the
 * preview tab navigates to the journey page — two portal tabs, one out of date. With it,
 * the preview tab announces the submission and closes itself; the original tab (the one
 * the investor started in) moves on to the journey page. Same-origin only.
 */
const CHANNEL = 'facilon-journey-submit';
const STORAGE_KEY = 'facilon.journeySubmitted';

export interface JourneySubmittedSignal {
  journeyId: string | null;
  /** Service-agent proxy investor id when the submit happened in SA mode. */
  saInvestorId: number | null;
  at: number;
}

export function announceJourneySubmitted(signal: Omit<JourneySubmittedSignal, 'at'>): void {
  const payload: JourneySubmittedSignal = { ...signal, at: Date.now() };
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage(payload);
    bc.close();
  } catch { /* no BroadcastChannel — storage fallback below */ }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* storage unavailable */ }
}

/** Subscribe; returns an unsubscribe function. */
export function onJourneySubmitted(handler: (s: JourneySubmittedSignal) => void): () => void {
  let bc: BroadcastChannel | null = null;
  try {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e) => handler(e.data as JourneySubmittedSignal);
  } catch { bc = null; }
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try { handler(JSON.parse(e.newValue) as JourneySubmittedSignal); } catch { /* ignore */ }
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('storage', onStorage);
    bc?.close();
  };
}
